import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ImmigrationRole = 'applicant' | 'dependent' | 'beneficiary' | 'petitioner' | 'payer' | 'poc';

export interface ItemContact {
  id: string;
  item_id: string;
  contact_id: string;
  immigration_role: ImmigrationRole;
  dependent_of: string | null;
  is_payer: boolean;
  is_point_of_contact: boolean;
  note: string | null;
  migrated_to_case_id: string | null;
  created_at: string;
  created_by: string | null;
}

export interface ItemContactInsert {
  item_id: string;
  contact_id: string;
  immigration_role: ImmigrationRole;
  dependent_of?: string | null;
  is_payer?: boolean;
  is_point_of_contact?: boolean;
  note?: string | null;
}

export interface ItemContactUpdate {
  immigration_role?: ImmigrationRole;
  dependent_of?: string | null;
  is_payer?: boolean;
  is_point_of_contact?: boolean;
  note?: string | null;
}

export interface ItemContactWithDetails extends ItemContact {
  contact: {
    id: string;
    first_name: string;
    last_name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
  };
  relationship_type?: string;
  principal_name?: string;
}

export function useItemContacts(itemId?: string) {
  const queryClient = useQueryClient();

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['item-contacts', itemId],
    queryFn: async () => {
      if (!itemId) return [];
      
      // Buscar item_contacts com dados do contato
      const { data: itemContacts, error } = await supabase
        .from('item_contacts')
        .select(`
          *,
          contact:contacts!item_contacts_contact_id_fkey(id, first_name, last_name, primary_email, primary_phone)
        `)
        .eq('item_id', itemId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Para dependentes, buscar o tipo de relacionamento de contact_relationships
      const contactsWithRelationships = await Promise.all(
        (itemContacts || []).map(async (ic: any) => {
          if (ic.dependent_of) {
            // Buscar relacionamento
            const { data: rel } = await supabase
              .from('contact_relationships')
              .select('relationship_type')
              .eq('from_contact_id', ic.dependent_of)
              .eq('to_contact_id', ic.contact_id)
              .single();
            
            // Buscar nome do principal
            const { data: principal } = await supabase
              .from('contacts')
              .select('first_name, last_name')
              .eq('id', ic.dependent_of)
              .single();
            
            return { 
              ...ic, 
              relationship_type: rel?.relationship_type,
              principal_name: principal ? `${principal.first_name} ${principal.last_name || ''}`.trim() : null
            };
          }
          return ic;
        })
      );

      return contactsWithRelationships as ItemContactWithDetails[];
    },
    enabled: !!itemId,
  });

  const addContact = useMutation({
    mutationFn: async (data: ItemContactInsert) => {
      const { data: result, error } = await supabase
        .from('item_contacts')
        .insert(data)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-contacts', itemId] });
      toast.success('Pessoa adicionada ao item');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao adicionar pessoa');
      console.error(error);
    },
  });

  const updateContact = useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & ItemContactUpdate) => {
      const { data: result, error } = await supabase
        .from('item_contacts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-contacts', itemId] });
      toast.success('Pessoa atualizada');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao atualizar pessoa');
      console.error(error);
    },
  });

  const removeContact = useMutation({
    mutationFn: async (contactId: string) => {
      const { error } = await supabase
        .from('item_contacts')
        .delete()
        .eq('id', contactId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-contacts', itemId] });
      toast.success('Pessoa removida do item');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao remover pessoa');
      console.error(error);
    },
  });

  // Função para criar/atualizar relacionamento familiar
  const ensureFamilyRelationship = async (
    fromContactId: string,
    toContactId: string,
    relationshipType: string
  ) => {
    // Verificar se já existe relacionamento
    const { data: existing } = await supabase
      .from('contact_relationships')
      .select('id')
      .eq('from_contact_id', fromContactId)
      .eq('to_contact_id', toContactId)
      .maybeSingle();

    if (existing) {
      // Atualizar tipo se necessário
      const { error } = await supabase
        .from('contact_relationships')
        .update({ relationship_type: relationshipType })
        .eq('id', existing.id);
      if (error) throw error;
    } else {
      // Criar novo relacionamento
      const { error } = await supabase
        .from('contact_relationships')
        .insert({
          from_contact_id: fromContactId,
          to_contact_id: toContactId,
          relationship_kind: 'family',
          relationship_type: relationshipType,
        });
      if (error) throw error;
    }

    // Invalidar queries de relacionamentos
    queryClient.invalidateQueries({ queryKey: ['contact-relationships'] });
  };

  // Helpers
  const applicant = contacts.find(c => c.immigration_role === 'applicant');
  const dependents = contacts.filter(c => c.immigration_role === 'dependent');
  const payer = contacts.find(c => c.is_payer);
  const pointOfContact = contacts.find(c => c.is_point_of_contact);

  return {
    contacts,
    isLoading,
    applicant,
    dependents,
    payer,
    pointOfContact,
    addContact: addContact.mutateAsync,
    updateContact: updateContact.mutateAsync,
    removeContact: removeContact.mutateAsync,
    ensureFamilyRelationship,
    isAdding: addContact.isPending,
    isUpdating: updateContact.isPending,
    isRemoving: removeContact.isPending,
  };
}
